import axios, { AxiosInstance } from 'axios';
import { config } from '../config';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper method for user-friendly error messages
  private getErrorMessage(status: number, identifier: string): string {
    switch (status) {
      case 400:
        if (identifier === 'subscribe') {
          return 'Please provide a valid email address';
        }
        if (identifier === 'update-user-mailing-lists') {
          return 'Invalid mailing list operation';
        }
        if (identifier === 'batch-update-email-status') {
          return 'Invalid email status update';
        }
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized to perform this action';
      case 403:
        return 'Access denied. Please check your permissions.';
      case 404:
        return 'The requested resource was not found';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed. Please try again.`;
    }
  }

  // Newsletter methods
  async getNewsletters() {
    return this.callBackendOperation('list-newsletters');
  }

  async getNewsletter(id: string) {
    const response = await this.api.get(config.endpoints.newsletter(id));
    return response.data;
  }

  async createNewsletter(data: any) {
    const response = await this.api.post(config.endpoints.createNewsletter, data);
    return response.data;
  }

  async updateNewsletter(id: string, data: any) {
    const response = await this.api.put(config.endpoints.updateNewsletter(id), data);
    return response.data;
  }

  async deleteNewsletter(id: string) {
    const response = await this.api.delete(config.endpoints.deleteNewsletter(id));
    return response.data;
  }

  async sendNewsletter(id: string, recipientList: string[]) {
    const response = await this.api.post(config.endpoints.sendNewsletter(id), {
      recipients: recipientList
    });
    return response.data;
  }

  async getNewsletterTracking(id: string) {
    const response = await this.api.get(config.endpoints.tracking(id));
    return response.data;
  }

  // Auth methods (Legacy - use new auth flow in onboarding)
  async googleAuth(credential: string) {
    // This method is kept for backward compatibility with newsletter functionality
    // New auth flow uses the onboarding process
    const response = await this.api.post('/auth/google', {
      credential
    });
    return response.data;
  }

  async refreshToken() {
    const response = await this.api.post(config.endpoints.auth.refresh);
    return response.data;
  }

  async logout() {
    const response = await this.api.post(config.endpoints.auth.logout);
    localStorage.removeItem('authToken');
    return response.data;
  }

  // Backend operation method with test mode fallback
  async callBackendOperation(identifier: string, data: any = {}) {
    try {
      // Always use test mode in development
      const isDevelopment = process.env.NODE_ENV === 'development';
      const url = isDevelopment ? config.API_BASE_URL + '?test=true' : config.API_BASE_URL;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier,
          ...data
        }),
        credentials: 'include'
      });

      if (!response.ok && !isDevelopment) {
        // Try with test mode if authentication fails in production
        const testResponse = await fetch(config.API_BASE_URL + '?test=true', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            identifier,
            ...data
          })
        });

        if (!testResponse.ok) {
          const errorData = await testResponse.json().catch(() => ({}));
          const errorMessage = errorData.error || `Request failed with status ${testResponse.status}`;
          throw new Error(errorMessage);
        }

        return await testResponse.json();
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Backend operation ${identifier} failed:`, error);
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      
      // If it's already a user-friendly error message, pass it through
      if (error.message && !error.message.includes('API request failed')) {
        throw error;
      }
      
      // Fallback error message
      throw new Error('Something went wrong. Please try again later.');
    }
  }

  // Get newsletter analytics
  async getNewsletterAnalytics(newsletterId: string) {
    try {
      const response = await this.callBackendOperation('get-newsletter-analytics', {
        newsletterId
      });
      return response;
    } catch (error: any) {
      console.error('Failed to get newsletter analytics:', error);
      throw error;
    }
  }

  // Get newsletter HTML preview with processed template variables
  async getNewsletterHtmlPreview(newsletterId: string, companyId: string = 'darlingmuesco') {
    try {
      const response = await this.callBackendOperation('preview-newsletter-html', {
        newsletterId,
        companyId
      });
      return response;
    } catch (error: any) {
      console.error('Failed to get newsletter HTML preview:', error);
      throw error;
    }
  }

  // Get email list for newsletters
  async getEmailList(newsletterId: string, isTestSend: boolean = false) {
    return this.callBackendOperation('get-email-list', {
      newsletterId,
      isTestSend
    });
  }

  // Get mailing lists
  async getMailingLists() {
    // Use the new get-all-subscribers endpoint for mailing list management
    const response = await this.callBackendOperation('get-all-subscribers', {
      isTestSend: false
    });
    return response;
  }

  // Update subscriber status
  async updateSubscriberStatus(email: string, status: 'SUBSCRIBED' | 'UNSUBSCRIBED') {
    return this.callBackendOperation('update-email-status', {
      email,
      status,
      isTestSend: false
    });
  }

  // Batch update subscriber statuses
  async batchUpdateSubscriberStatus(updates: Array<{email: string, status: 'SUBSCRIBED' | 'UNSUBSCRIBED'}>) {
    return this.callBackendOperation('batch-update-email-status', {
      updates,
      isTestSend: false
    });
  }
}

const apiService = new ApiService();
export default apiService;